import { api } from "../../shared/api/client";
import type {
  ContractTierInfoResponse,
  CustomerDetailResponse,
  DeviceListResponse,
  DeviceLookupResponse,
  EvidenceRequest,
  EvidenceResponse,
  InstallationApproveResponse,
  InstallationCorrectionRequest,
  InstallationDetailResponse,
  InstallationListResponse,
  InstallationPhotoType,
  InstallationPhotoUploadResponse,
  InstallationSimpleResponse,
  InstallationSubmitResponse,
  InstallationStatus,
  ProductDetailResponse,
  UserListResponse,
} from "./types";

/**
 * Installation-domain API calls. Wizard endpoints → installation-service;
 * enrichment reads → customer- / device- / product- / payment- / auth-service
 * (all behind the same /api/v1 gateway base).
 */

export function listInstallations(params: {
  status?: InstallationStatus;
  assignedToMe?: boolean;
  perPage?: number;
}) {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.assignedToMe) qs.set("assigned_to", "me");
  if (params.perPage) qs.set("per_page", String(params.perPage));
  const suffix = qs.size > 0 ? `?${qs.toString()}` : "";
  return api.get<InstallationListResponse>(`/installations${suffix}`);
}

export function getInstallation(id: number) {
  return api.get<InstallationDetailResponse>(`/installations/${id}`);
}

export function postEvidence(id: number, body: EvidenceRequest) {
  return api.post<EvidenceResponse>(`/installations/${id}/evidence`, body);
}

/** Photo upload — multipart fields are `photo` + `photo_type` (installations.yaml). */
export function uploadInstallationPhoto(
  id: number,
  photoType: InstallationPhotoType,
  file: File,
) {
  const form = new FormData();
  form.append("photo", file, file.name);
  form.append("photo_type", photoType);
  return api.upload<InstallationPhotoUploadResponse>(`/installations/${id}/photos`, form);
}

/** Idempotent submit — server persists the Idempotency-Key. */
export function submitInstallation(id: number, idempotencyKey: string) {
  return api.post<InstallationSubmitResponse>(`/installations/${id}/submit`, {}, idempotencyKey);
}

/** Idempotent approve — replay returns the SAME token (server-side). */
export function approveInstallation(id: number) {
  return api.post<InstallationApproveResponse>(`/installations/${id}/approve`, {});
}

export function requestInstallationCorrection(id: number, body: InstallationCorrectionRequest) {
  return api.post<InstallationSimpleResponse>(`/installations/${id}/correction`, body);
}

export function markTokenDelivered(id: number) {
  return api.post<InstallationSimpleResponse>(`/installations/${id}/token-delivered`, {});
}

// ── Enrichment reads ─────────────────────────────────────────────────────────

export function getCustomerDetail(id: number) {
  return api.get<CustomerDetailResponse>(`/customers/${id}`);
}

export function getProductDetail(id: number) {
  return api.get<ProductDetailResponse>(`/products/${id}`);
}

export function lookupDeviceBySerial(serial: string) {
  return api.get<DeviceLookupResponse>(`/devices/${encodeURIComponent(serial)}`);
}

export function listDevices(perPage = 200) {
  return api.get<DeviceListResponse>(`/devices?per_page=${perPage}`);
}

export function listUsers(perPage = 200) {
  return api.get<UserListResponse>(`/users?per_page=${perPage}`);
}

export function getContractTierInfo(contractId: number) {
  return api.get<ContractTierInfoResponse>(`/contracts/${contractId}/tier-info`);
}
